import type {
  PopStoreCreatorBusinessType,
  RegistrationVenueType,
} from '@ps/models/lib/profile';
import type { CreatorInfo } from '@ps/types/creator';

/** Store fields that source a {@link CreatorInfo} — the storefront name + bio.
 *  `bio` is the EDITED source of truth: editing the creator bio writes the store
 *  doc, so it wins over `profile.bio`. A `StoreFront` / `StoreSlim` satisfies this. */
interface CreatorStoreFields {
  name?: string;
  bio?: string;
}

/** Profile fields that source a {@link CreatorInfo} — identity (id / url code /
 *  avatar) plus the name / bio fallbacks. A `Profile` / `ProfileSlim` satisfies
 *  this. */
interface CreatorProfileFields {
  id: string;
  urlCode?: string;
  storeFrontName?: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  bio?: string;
  businessType?: PopStoreCreatorBusinessType;
  registrationVenue?: RegistrationVenueType;
  followersSettings?: { description?: string; active?: boolean };
  signInMessage?: {
    heading: string;
    subject: string;
    ctaText: string;
  };
}

/**
 * Build the unified {@link CreatorInfo} from a creator's store + profile docs —
 * the SINGLE place this projection lives (replaces the scattered inline literals
 * and the per-surface adapters).
 *
 * Field sourcing:
 *  - `id` / `urlCode` / `avatar` — identity, from the profile.
 *  - `name` — the storefront name, else the profile's store-front name, else the
 *    person's name.
 *  - `bio` — `store.bio` FIRST (editing the creator bio only writes the store
 *    doc, so the profile copy goes stale), falling back to `profile.bio`.
 *
 * `profile` (the identity source) is required; `store` is optional — pass it
 * whenever `store.bio` / `store.name` matter (the edited source of truth), and
 * omit / pass `null` on the less-critical surfaces (e.g. the video footer, or the
 * visitor PDP guard kept firestore-free) that only carry the profile.
 */
export function creatorInfoFromStoreProfile(
  profile: CreatorProfileFields,
  store?: CreatorStoreFields | null,
): CreatorInfo {
  return {
    id: profile.id,
    urlCode: profile.urlCode ?? '',
    name:
      store?.name ||
      profile.storeFrontName ||
      [profile.firstName, profile.lastName].filter(Boolean).join(' '),
    avatar: profile.avatar,
    bio: store?.bio ?? profile.bio,
    businessType: profile.businessType,
    registrationVenue: profile.registrationVenue,
    signInMessage: profile.signInMessage,
    followersSettings: profile.followersSettings,
  };
}
